import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Message } from '@arco-design/web-react';
import axios, { AxiosError } from 'axios';

import { endpoints } from '../../api/endpoints';
import { GuestConvertRole } from '../../constants/guestIntake';
import { Logo } from '../../components/Logo';
import { LanguageSelect } from '../../components/LanguageSelect/LanguageSelect';
import { routes } from '../../routes/routes';
import { RootState } from '../../store/store';
import { setUser } from '../../store/userSlice';
import { trackEvent } from '../../analytics/track';
import { getAuthLogoType } from '../../theme';
import { getBoxBackgroundColor, getTextColor } from '../../utils/cssVariables';
import { ownProfilePathAfterUpdate } from '../../utils/ownProfilePath';

import { GuestRoleChoicePanel } from './GuestRoleChoicePanel';
import './GuestIntakeForm.css';

type GuestIntakeConvertStepProps = {
  initialRole?: GuestConvertRole | null;
};

export function GuestIntakeConvertStep({ initialRole = null }: GuestIntakeConvertStepProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user);
  const expired = Boolean(user?.guestExpired);
  const [submitting, setSubmitting] = useState(false);
  const [submittingRole, setSubmittingRole] = useState<GuestConvertRole | null>(null);
  const convertingRef = useRef(false);
  const autoStartedRef = useRef(false);

  const convertRole = useCallback(
    async (role: GuestConvertRole) => {
      if (!user || convertingRef.current) {
        return false;
      }

      convertingRef.current = true;
      setSubmitting(true);
      setSubmittingRole(role);
      try {
        const response = await axios.post(endpoints.guestConvert, { role });
        const convertedUser = { ...user, ...response.data };
        dispatch(setUser(convertedUser));
        trackEvent("guest_convert", { role });
        Message.success({
          content: t('guest_conversion.success_message', {
            role: t(role === 'mentor' ? 'common.mentor' : 'common.mentee'),
          }),
          closable: true,
        });
        const destination = ownProfilePathAfterUpdate(convertedUser, convertedUser.id);
        navigate(destination, { replace: true });
        return true;
      } catch (error) {
        if (error instanceof AxiosError) {
          const message = (error.response?.data as { error?: string } | undefined)?.error;
          if (message === 'event intake disabled') {
            Message.error(t('notification_messages.event_intake_disabled'));
            return false;
          }
          if (message === 'guest window expired') {
            Message.error(t('notification_messages.guest_window_expired'));
            return false;
          }
          if (message === 'complete guest intake before converting') {
            Message.error(t('guest_conversion.intake_required'));
            navigate(routes.GUEST_INTAKE, { replace: true });
            return false;
          }
        }
        Message.error(t('notification_messages.unexpected_error'));
        return false;
      } finally {
        convertingRef.current = false;
        setSubmitting(false);
        setSubmittingRole(null);
      }
    },
    [dispatch, navigate, t, user],
  );

  useEffect(() => {
    if (!initialRole || autoStartedRef.current || !user) {
      return;
    }

    autoStartedRef.current = true;
    void convertRole(initialRole).then((started) => {
      if (!started) {
        autoStartedRef.current = false;
      }
    });
  }, [convertRole, initialRole, user]);

  return (
    <div
      className="guest-intake-form"
      style={{
        backgroundColor: getBoxBackgroundColor(),
        boxSizing: 'border-box',
        maxWidth: 480,
        width: 'calc(100% - 32px)',
        margin: '16px auto',
        padding: '20px 16px',
        borderRadius: 8,
      }}
    >
      <div className="guest-intake-form__brand">
        <Logo variant="auth" style={{ marginTop: 10 }} type={getAuthLogoType()} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            paddingTop: 12,
            margin: '0 auto',
          }}
        >
          <LanguageSelect color={getTextColor()} />
        </div>
      </div>

      <GuestRoleChoicePanel
        expired={expired}
        submitting={submitting}
        submittingRole={submittingRole}
        onSelectRole={(role) => void convertRole(role)}
      />
    </div>
  );
}
